<script setup lang="ts">
import { getAssetsUrlForCDN } from '@/utils/template';

// Badge central do Clube de Fidelidade (aparece só logado). Mesmo formato do
// HomeBadge (círculo azul elevado no bump da barra), mas com o logo do Clube e
// abre o hub em vez de navegar.
const store = useLoyaltyHubStore();

const logo = getAssetsUrlForCDN('/assets/images/loyalty-hub/badge-logo.png');

function onClick() {
  store.open();
}

// A badge só existe pra quem pode abrir o hub, então é o gatilho natural pra
// aquecer as artes do topo do painel — no ócio, sem disputar rede com a página.
// Sem isso a primeira abertura pedia header + cards na hora e o conteúdo ia
// aparecendo em cascata.
onMounted(() => {
  const warm = () => store.warmAssets();

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(warm, { timeout: 3000 });

    return;
  }

  // Safari antigo não tem requestIdleCallback.
  setTimeout(warm, 1500);
});
</script>

<template>
  <div
    class="badge-wrapper"
    @click="onClick"
  >
    <div class="badge">
      <div class="glow" />
      <div
        aria-label="Clube de Fidelidade"
        class="logo"
        role="img"
        :style="{
          backgroundImage: `url(${logo})`,
          '--badge-logo': `url(${logo})`,
        }"
      >
        <!-- O brilho vive dentro do .logo pra herdar o bob/esmagamento e ficar
             colado no emblema. O mask recorta a faixa no contorno da arte. -->
        <div class="shine">
          <i />
        </div>
      </div>
    </div>
    <div class="spacer" />
  </div>
</template>

<style scoped lang="scss">
.badge-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  cursor: pointer;
  z-index: 2;
}

.badge {
  position: absolute;
  top: -26px;
  width: 62px;
  height: 62px;
  border-radius: 50%;
  background: radial-gradient(
    ellipse 110% 130% at 50% 18%,
    #2c6cfb 0%,
    #1a56d6 48%,
    #0a2a72 100%
  );
  box-shadow:
    0 0 18px rgba(44, 108, 251, 0.55),
    0 4px 20px rgba(10, 42, 114, 0.6);

  .glow {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    box-shadow: inset 0 3px 10px rgba(255, 255, 255, 0.3);
    z-index: 1;
  }

  // Emblema como no Figma: caixa 65.9x56.17 alinhada ao topo do círculo, com o
  // image-fill do Figma (-6.126px -9.243px / 118.159% 138.594% no-repeat). O
  // asset (badge-logo.png) é o do Figma, que já traz padding/glow transparente
  // embutido — por isso o emblema fica contido com margem azul (não estoura).
  .logo {
    position: absolute;
    top: 0;
    left: 50%;
    z-index: 2;
    width: 65.9px;
    height: 56.17px;
    transform: translateX(-50%);
    // Origem embaixo: o esmagamento achata contra o "chão" em vez de encolher
    // pro centro, que é o que vende o peso de borracha.
    transform-origin: 50% 100%;
    background-position: -6.126px -9.243px;
    background-size: 118.159% 138.594%;
    background-repeat: no-repeat;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.35));
    animation: badge-bounce 2.4s infinite;
    will-change: transform;
  }
}

// Substitui o badge-logo.gif (899KB, 77 frames): a arte animada era só o
// emblema flutuando ~6px e um brilho passando, os dois de graça em transform.
// Sobe desacelerando e cai acelerando (gravidade), e no toque esmaga em
// scaleX/scaleY antes de voltar com um rebote elástico.
@keyframes badge-bounce {
  0% {
    transform: translateX(-50%) translateY(0) scale(1, 1);
    animation-timing-function: cubic-bezier(0.33, 0, 0.35, 1);
  }

  38% {
    transform: translateX(-50%) translateY(-5px) scale(0.985, 1.02);
    animation-timing-function: cubic-bezier(0.5, 0, 0.9, 0.35);
  }

  72% {
    transform: translateX(-50%) translateY(0) scale(1.012, 0.985);
    animation-timing-function: cubic-bezier(0.2, 0, 0.4, 1);
  }

  // Pico do esmagamento. O scaleX fica em 1.05 porque o emblema (65.9px) já é
  // mais largo que o círculo (62px): acima disso o letreiro estoura a borda.
  // O achatamento vem do scaleY, que é onde o olho lê a deformação de borracha.
  80% {
    transform: translateX(-50%) translateY(0) scale(1.05, 0.89);
    animation-timing-function: cubic-bezier(0.3, 0, 0.2, 1);
  }

  88% {
    transform: translateX(-50%) translateY(-1.5px) scale(0.975, 1.035);
    animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1);
  }

  100% {
    transform: translateX(-50%) translateY(0) scale(1, 1);
  }
}

// Mesmo image-fill do .logo, agora como máscara: a faixa só aparece sobre os
// pixels opacos do emblema, então o brilho acompanha o contorno da arte.
// Sem máscara a faixa viraria um retângulo branco cru em cima do emblema, então
// o brilho só entra onde o recorte funciona (o @supports abaixo religa).
.shine {
  display: none;
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  mask-image: var(--badge-logo);
  mask-position: -6.126px -9.243px;
  mask-size: 118.159% 138.594%;
  mask-repeat: no-repeat;
  -webkit-mask-image: var(--badge-logo);
  -webkit-mask-position: -6.126px -9.243px;
  -webkit-mask-size: 118.159% 138.594%;
  -webkit-mask-repeat: no-repeat;

  // Faixa estreita (11px numa caixa de 65.9px) e com o pico concentrado no
  // meio: mais larga que isso ela lava o letreiro branco em vez de parecer
  // reflexo.
  i {
    position: absolute;
    top: -50%;
    left: 0;
    width: 11px;
    height: 200%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.9) 46%,
      rgba(255, 255, 255, 0.9) 54%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: badge-shine 3.2s linear infinite;
    will-change: transform, opacity;
  }
}

@supports (mask-image: none) or (-webkit-mask-image: none) {
  .shine {
    display: block;
  }
}

// Varredura rápida e depois pausa longa — no GIF o brilho também não era
// contínuo, aparecia em passadas espaçadas.
@keyframes badge-shine {
  0%,
  8% {
    opacity: 0;
    transform: translate3d(-16px, 0, 0) rotate(20deg);
  }

  16%,
  34% {
    opacity: 0.7;
  }

  42%,
  100% {
    opacity: 0;
    transform: translate3d(78px, 0, 0) rotate(20deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .badge .logo,
  .shine i {
    animation: none;
  }
}

.spacer {
  width: 62px;
  height: 30px;
}
</style>
